import React, { FC, useEffect, useLayoutEffect, useRef } from 'react';
import styled from 'styled-components';

interface Props {
	// The height is animated when this changes, e.g. when the children are swapped for different content
	animationKey: string | number;
	duration?: number;
	// Extra space (px) kept visible around the content while animating, e.g. for outlines or box shadow
	overflowMargin?: number;
	children: React.ReactNode;
}

// Use layout effect in the browser only, to avoid the SSR warning
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const AnimatedHeight: FC<Props> = ({ animationKey, duration = 300, overflowMargin = 0, children }) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const contentRef = useRef<HTMLDivElement>(null);
	const lastHeight = useRef<number>(null);
	const isAnimating = useRef(false);
	const isFirstRender = useRef(true);

	// Track the content height, and retarget a running animation if the new content keeps growing
	useEffect(() => {
		const content = contentRef.current;
		if (!content || typeof ResizeObserver === 'undefined') return;
		const observer = new ResizeObserver(() => {
			lastHeight.current = content.offsetHeight;
			if (isAnimating.current && containerRef.current) {
				containerRef.current.style.height = `${content.offsetHeight}px`;
			}
		});
		observer.observe(content);
		lastHeight.current = content.offsetHeight;
		return () => observer.disconnect();
	}, []);

	useIsomorphicLayoutEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			return;
		}
		const container = containerRef.current;
		const content = contentRef.current;
		const from = lastHeight.current;
		const to = content?.offsetHeight;
		const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
		if (!container || from === null || to === undefined || from === to || prefersReducedMotion) return;

		const reset = () => {
			isAnimating.current = false;
			container.style.transition = '';
			container.style.height = '';
			container.style.overflow = '';
			container.removeEventListener('transitionend', onTransitionEnd);
			clearTimeout(fallback);
		};
		const onTransitionEnd = (event: TransitionEvent) => {
			if (event.target === container && event.propertyName === 'height') reset();
		};

		isAnimating.current = true;
		container.style.transition = 'none';
		container.style.height = `${from}px`;
		container.style.overflow = 'hidden';
		// Force a reflow so the browser starts the transition from the old height
		void container.offsetHeight;
		container.style.transition = `height ${duration}ms ease`;
		container.style.height = `${to}px`;

		container.addEventListener('transitionend', onTransitionEnd);
		// Safety net in case transitionend never fires (e.g. the element is hidden)
		const fallback = setTimeout(reset, duration * 3);
		return reset;
	}, [animationKey]);

	return (
		<Container ref={containerRef} $overflowMargin={overflowMargin}>
			<div ref={contentRef}>{children}</div>
		</Container>
	);
};

// The padding and negative margin cancel out, they only widen the area that overflow: hidden clips to
const Container = styled.div<{ $overflowMargin: number }>`
	position: relative;
	box-sizing: content-box;
	padding: ${({ $overflowMargin }) => $overflowMargin}px;
	margin: ${({ $overflowMargin }) => -$overflowMargin}px;
`;

export default AnimatedHeight;
